import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { setTrialExpirationHandler } from '../services/ApiClient'

interface TrialExpirationInfo {
  isExpired: boolean
  message?: string
  trialEndedAt?: string
}

interface TrialExpirationContextType {
  trialExpired: TrialExpirationInfo
  setTrialExpired: (info: TrialExpirationInfo) => void
  clearTrialExpired: () => void
}

const TrialExpirationContext = createContext<TrialExpirationContextType | undefined>(undefined)

export const TrialExpirationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [trialExpired, setTrialExpiredState] = useState<TrialExpirationInfo>({
    isExpired: false
  })

  const setTrialExpired = useCallback((info: TrialExpirationInfo) => {
    setTrialExpiredState(info)
  }, [])

  const clearTrialExpired = useCallback(() => {
    setTrialExpiredState({ isExpired: false })
  }, [])

  // Set up ApiClient handler when provider mounts
  useEffect(() => {
    setTrialExpirationHandler((message: string, trialEndedAt?: string) => {
      setTrialExpiredState({
        isExpired: true,
        message,
        trialEndedAt
      })
    })

    // Cleanup handler on unmount
    return () => {
      setTrialExpirationHandler(() => {})
    }
  }, [])

  return (
    <TrialExpirationContext.Provider value={{ trialExpired, setTrialExpired, clearTrialExpired }}>
      {children}
    </TrialExpirationContext.Provider>
  )
}

export const useTrialExpiration = () => {
  const context = useContext(TrialExpirationContext)
  if (!context) {
    throw new Error('useTrialExpiration must be used within TrialExpirationProvider')
  }
  return context
}
